import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Newspaper, Calendar, ChevronRight, Loader2, Image as ImageIcon, ChevronLeft } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const News = () => {
  const { t, language } = useLanguage();
  const [selectedNews, setSelectedNews] = useState(null);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [galleryImages, setGalleryImages] = useState([]);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/news`);
      setNews(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching news:', err);
      setError(language === 'sl' ? 'Napaka pri nalaganju novic' : 'Error loading news');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'sl' ? 'sl-SI' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Transform API data to component format
  const getTitle = (item) => language === 'sl' ? item.title_sl : item.title_en;
  const getExcerpt = (item) => language === 'sl' ? item.excerpt_sl : item.excerpt_en;
  const getContent = (item) => language === 'sl' ? item.content_sl : item.content_en;

  // Get all images for a news item
  const getAllImages = (item) => {
    const images = [];
    if (item.image) images.push(item.image);
    if (item.images && item.images.length > 0) {
      images.push(...item.images);
    }
    return images;
  };

  const openGallery = (item, startIndex = 0) => {
    const images = getAllImages(item);
    if (images.length > 0) {
      setGalleryImages(images);
      setGalleryIndex(startIndex);
      setGalleryOpen(true);
    }
  };

  return (
    <section id="news" className="py-24 bg-stone-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 text-emerald-400 mb-4">
            <Newspaper className="w-5 h-5" />
            <span className="text-sm font-semibold tracking-wider uppercase">
              {t.news.title}
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white">
            {t.news.title}
          </h2>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="text-center py-12">
            <p className="text-stone-400">{error}</p>
            <Button
              variant="outline"
              onClick={fetchNews}
              className="mt-4 border-emerald-600 text-emerald-400 hover:bg-emerald-600/20"
            >
              {language === 'sl' ? 'Poskusi znova' : 'Try again'}
            </Button>
          </div>
        )}

        {/* News Grid */}
        {!loading && !error && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {news.map((item) => {
              const allImages = getAllImages(item);
              return (
                <Card
                  key={item.id}
                  className="bg-stone-900/50 border-stone-800 overflow-hidden hover:bg-stone-900 hover:border-emerald-600/30 transition-all duration-300 group flex flex-col"
                >
                  {/* Main Image */}
                  {item.image && (
                    <div 
                      className="relative h-48 overflow-hidden cursor-pointer"
                      onClick={() => openGallery(item, 0)}
                    >
                      <img
                        src={item.image}
                        alt={getTitle(item)}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {allImages.length > 1 && (
                        <div className="absolute bottom-2 right-2 bg-black/60 px-2 py-1 rounded-full text-xs text-white flex items-center gap-1">
                          <ImageIcon className="w-3 h-3" />
                          {allImages.length}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center justify-between mb-4">
                      <Badge
                        variant="outline"
                        className={`${
                          item.category === 'event'
                            ? 'border-amber-500/50 text-amber-400'
                            : 'border-emerald-500/50 text-emerald-400'
                        }`}
                      >
                        {item.category === 'event' ? t.news.event : t.news.newsLabel}
                      </Badge>
                      <div className="flex items-center gap-2 text-sm text-stone-500">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(item.date)}</span>
                      </div>
                    </div>

                    <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-emerald-300 transition-colors">
                      {getTitle(item)}
                    </h3>

                    <p className="text-stone-400 mb-4 flex-1">
                      {getExcerpt(item)}
                    </p>

                    <Button
                      variant="ghost"
                      onClick={() => setSelectedNews(item)}
                      className="w-full justify-between text-emerald-400 hover:text-emerald-300 hover:bg-emerald-600/10 group/btn"
                    >
                      {t.news.readMore}
                      <ChevronRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && news.length === 0 && (
          <div className="text-center py-12">
            <p className="text-stone-400">
              {language === 'sl' ? 'Ni novic' : 'No news available'}
            </p>
          </div>
        )}

        {/* News Detail Dialog */}
        <Dialog open={!!selectedNews} onOpenChange={() => setSelectedNews(null)}>
          <DialogContent className="max-w-3xl bg-stone-900 border-stone-800 max-h-[90vh] overflow-y-auto">
            {selectedNews && (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <Badge
                      variant="outline"
                      className={`${
                        selectedNews.category === 'event'
                          ? 'border-amber-500/50 text-amber-400'
                          : 'border-emerald-500/50 text-emerald-400'
                      }`}
                    >
                      {selectedNews.category === 'event'
                        ? t.news.event
                        : t.news.newsLabel}
                    </Badge>
                    <span className="text-sm text-stone-500">
                      {formatDate(selectedNews.date)}
                    </span>
                  </div>
                  <DialogTitle className="text-2xl text-white">
                    {getTitle(selectedNews)}
                  </DialogTitle>
                </DialogHeader>

                {/* Images in dialog */}
                {(() => {
                  const images = getAllImages(selectedNews);
                  if (images.length === 0) return null;
                  
                  return (
                    <div className="mt-4">
                      {/* Main image */}
                      {selectedNews.image && (
                        <img
                          src={selectedNews.image}
                          alt={getTitle(selectedNews)}
                          className="w-full h-64 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => openGallery(selectedNews, 0)}
                        />
                      )}
                      
                      {/* Gallery thumbnails */}
                      {images.length > 1 && (
                        <div className="flex gap-2 mt-3 flex-wrap">
                          {images.slice(selectedNews.image ? 1 : 0).map((img, idx) => (
                            <img
                              key={idx}
                              src={img}
                              alt={`${getTitle(selectedNews)} ${idx + 1}`}
                              className="w-20 h-20 object-cover rounded cursor-pointer border border-stone-700 hover:border-emerald-500 transition-colors"
                              onClick={() => openGallery(selectedNews, selectedNews.image ? idx + 1 : idx)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}

                <div className="mt-4">
                  <p className="text-stone-300 leading-relaxed whitespace-pre-line">
                    {getContent(selectedNews)}
                  </p>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Gallery Lightbox */}
        <Dialog open={galleryOpen} onOpenChange={setGalleryOpen}>
          <DialogContent className="bg-stone-900/95 border-stone-700 max-w-5xl p-2">
            <div className="relative">
              <img
                src={galleryImages[galleryIndex]}
                alt={`Slika ${galleryIndex + 1}`}
                className="w-full max-h-[75vh] object-contain rounded-lg"
              />
              {galleryImages.length > 1 && (
                <>
                  <button
                    onClick={() => setGalleryIndex((galleryIndex - 1 + galleryImages.length) % galleryImages.length)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center text-white transition-colors"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={() => setGalleryIndex((galleryIndex + 1) % galleryImages.length)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center text-white transition-colors"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 px-3 py-1 rounded-full text-white text-sm">
                {galleryIndex + 1} / {galleryImages.length}
              </div>
            </div>
            {galleryImages.length > 1 && (
              <div className="flex gap-2 justify-center mt-3 flex-wrap px-2 pb-2">
                {galleryImages.map((img, idx) => (
                  <img
                    key={idx}
                    src={img}
                    alt={`Thumbnail ${idx + 1}`}
                    className={`w-14 h-14 object-cover rounded cursor-pointer transition-all ${
                      idx === galleryIndex 
                        ? 'ring-2 ring-emerald-500 opacity-100' 
                        : 'opacity-60 hover:opacity-100'
                    }`}
                    onClick={() => setGalleryIndex(idx)}
                  />
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

export default News;
